// Shared @-mention primitives. Mentions are structured character ranges
// riding NEXT TO the text (never parsed back out of it), so any module can
// attach people to any kind of text body and submit `{ body, mentions }`
// to its backend.

export interface NxMention {
  accountId: number;
  name: string;
  initials: string;
  start: number;
  end: number;
  label: string;
}

// The wire shape mutations send (display fields resolved server-side).
export interface NxMentionInput {
  accountId: number;
  start: number;
  end: number;
  label: string;
}

// A pickable person. `id` is the module-local account id the consuming
// module submits; the candidate source is pluggable per module.
// Optional `shortName` / `initials` (e.g. employee short_name "JD") widen
// the @-picker filter beyond the display name.
export interface NxMentionCandidate {
  id: number;
  name: string;
  /** Explicit initials / Kürzel (e.g. "JD"). Falls back to deriveInitials(name). */
  initials?: string;
  /** Employee short_name or similar compact code. */
  shortName?: string;
}

export function deriveInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

/**
 * Match a picker query against a candidate: full name substring, first/last
 * word prefixes, derived or explicit initials (MS → Max Schneider), shortName.
 */
export function matchesMentionQuery(
  candidate: Pick<NxMentionCandidate, 'name' | 'initials' | 'shortName'>,
  rawQuery: string
): boolean {
  const q = (rawQuery || '').trim().toLowerCase();
  if (!q) return true;
  const name = (candidate.name || '').trim();
  const nameLower = name.toLowerCase();
  if (nameLower.includes(q)) return true;

  // Word-prefix: "jen deu" / "j d" style — each query token must prefix some word.
  const words = nameLower.split(/\s+/).filter(Boolean);
  const qTokens = q.split(/[\s.]+/).filter(Boolean);
  if (qTokens.length > 1 && qTokens.every((t) => words.some((w) => w.startsWith(t)))) {
    return true;
  }

  // Initials / Kürzel: "ms", "MS", "m.s." → Max Schneider
  const initials = (candidate.initials || deriveInitials(name))
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const qCompact = q.replace(/[^a-z0-9]/g, '');
  if (qCompact && initials && (initials === qCompact || initials.startsWith(qCompact))) {
    return true;
  }

  // First letters of every word concatenated (handles middle names too).
  // Guard qCompact: punctuation-only query (".") would otherwise match everyone
  // because every string startsWith('').
  if (qCompact && words.length >= 2) {
    const allInitials = words.map((w) => w[0]).join('');
    if (allInitials.startsWith(qCompact)) return true;
  }

  const short = (candidate.shortName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (short && qCompact && (short === qCompact || short.startsWith(qCompact))) {
    return true;
  }

  return false;
}
